import {match} from 'ts-pattern';

import {always} from 'ramda';
import {isNonEmptyString} from 'ramda-adjunct';

import {
  MazdaPreauthorizationScopeSchema,
  MazdaPreauthorizationWarrantyTypeSchema,
  PutServiceOrderIntegrationsMazdaPreauthorizationApiArg,
} from '@dms/api/workshop';

import {Nullish} from 'shared/web';

import {
  MAINTENANCE_DONE_RIGHT,
  MAINTENANCE_NOT_DONE_CORRECTLY,
  MazdaPreauthSendFormType,
} from '../types/MazdaPreauthSendFormType';
import {hasMazdaWarrantyInstallationDetails} from './hasMazdaWarrantyInstallationDetails';

type MazdaPreauthRequestBody = PutServiceOrderIntegrationsMazdaPreauthorizationApiArg['body'];

// Save and close submits raw form values without running validation, so every field may be missing.
type MazdaPreauthSendFormValues = Partial<MazdaPreauthSendFormType>;

const toNullableString = (value: string | Nullish) => {
  const trimmedValue = value?.trim();

  return isNonEmptyString(trimmedValue) ? trimmedValue : null;
};

const toScope = (value: string | Nullish) => {
  const result = MazdaPreauthorizationScopeSchema.safeParse(value);

  return result.success ? result.data : null;
};

const toWarrantyType = (value: string | Nullish) => {
  const result = MazdaPreauthorizationWarrantyTypeSchema.safeParse(value);

  return result.success ? result.data : null;
};

const toMaintenanceFlag = (value: string | Nullish) =>
  match<string | Nullish, boolean | null>(value)
    .with(MAINTENANCE_DONE_RIGHT, always(true))
    .with(MAINTENANCE_NOT_DONE_CORRECTLY, always(false))
    .otherwise(always(null));

export const mapMazdaPreauthFormToRequestBody = (
  data: MazdaPreauthSendFormValues
): MazdaPreauthRequestBody => {
  const hasInstallationDetails = hasMazdaWarrantyInstallationDetails(data.warrantyType);

  return {
    scope: toScope(data.scope),
    warrantyType: toWarrantyType(data.warrantyType),
    mileage: data.mileage ?? null,
    vehicleServicedAccordingToManufacturerSpecs: toMaintenanceFlag(
      data.vehicleServicedAccordingToManufacturerSpecs
    ),
    installationDate: hasInstallationDetails ? toNullableString(data.installationDate) : null,
    installationMileage: hasInstallationDetails ? (data.installationMileage ?? null) : null,
    installationRepairNo: hasInstallationDetails
      ? toNullableString(data.installationRepairNo)
      : null,
    causalPart: toNullableString(data.causalPart),
    specialCausalPart: data.isSpecialCausalPart ? toNullableString(data.specialCausalPart) : null,
    message: toNullableString(data.message),
  };
};
